import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { AUTH } from "@/constants/testIds";
import GoogleButton from "@/components/GoogleButton";
import AccessCodeChip from "@/components/AccessCodeChip";
import { api } from "@/lib/api";
import { ArrowUpRight, ShieldCheck } from "lucide-react";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [needs2fa, setNeeds2fa] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const performLogin = async (payload) => {
    // Bypass the auth context helper so we can send totp_code directly.
    const { data } = await api.post("/auth/login", payload);
    localStorage.setItem("atlas_token", data.token);
    localStorage.setItem("atlas_user", JSON.stringify(data.user));
    return data.user;
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success(`Welcome back, ${user.full_name.split(" ")[0]}!`);
      // Entrepreneurs go straight into Business DNA™ onboarding until it's complete.
      if (user.role === "entrepreneur") {
        try {
          const { data } = await api.get("/dna");
          const done = (data?.dna?.completed_sections || []).length;
          navigate(done >= 11 ? "/app" : "/app/onboarding");
        } catch {
          navigate("/app/onboarding");
        }
      } else {
        navigate("/app");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Sign in failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen grid-cols-1 lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between border-r border-slate-800 bg-slate-950 p-12 text-white">
        <Link to="/" className="flex items-center gap-2 w-fit">
          <div className="flex h-8 w-8 items-center justify-center rounded-md bg-white text-slate-950">
            <span className="font-display text-sm font-bold">A</span>
          </div>
          <div>
            <div className="font-display text-lg font-bold">ATLAS™</div>
            <div className="label-mono !text-[9px] !text-stone-500">The IXL Business Pro</div>
          </div>
        </Link>
        <div>
          <h2 className="font-display text-5xl font-bold leading-tight tracking-tight">
            The AI Operating System for Entrepreneurial Ecosystems.
          </h2>
          <p className="mt-6 max-w-md text-base text-stone-300">
            Sign in to continue building stronger businesses, measuring impact, and scaling your entrepreneurship program.
          </p>
        </div>
        <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-stone-500">
          Powered by AIH LLC
        </div>
      </div>
      <div className="flex items-center justify-center px-6 py-12 lg:px-16">
        <div className="w-full max-w-md">
          <div className="label-mono mb-3">Sign In</div>
          <h1 className="font-display text-4xl font-bold tracking-tight text-slate-950">Welcome back.</h1>
          <p className="mt-2 text-sm text-stone-600">Continue your journey to measurable impact.</p>
          <form onSubmit={submit} className="mt-8 space-y-4">
            <label className="block">
              <span className="label-mono block mb-1.5">Email</span>
              <input data-testid={AUTH.loginEmail} required type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} autoComplete="email" />
            </label>
            <label className="block">
              <span className="label-mono block mb-1.5">Password</span>
              <input data-testid={AUTH.loginPassword} required type="password" value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} autoComplete="current-password" />
            </label>
            {needs2fa && (
              <label className="block">
                <span className="label-mono flex items-center gap-1.5 mb-1.5"><ShieldCheck className="h-3 w-3 text-emerald-600" /> Two-Factor Code</span>
                <input
                  data-testid="login-totp-code"
                  autoFocus
                  required
                  inputMode="numeric"
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="000000"
                  className={`${inputCls} font-mono tracking-[0.4em] text-center`}
                />
              </label>
            )}
            <button data-testid={AUTH.loginSubmit} disabled={loading} type="submit" className="btn-primary w-full">
              {loading ? "Signing in…" : (<>Sign In <ArrowUpRight className="h-4 w-4" /></>)}
            </button>
          </form>
          <div className="my-6 flex items-center gap-3">
            <div className="h-px flex-1 bg-stone-200" />
            <span className="font-mono text-[10px] uppercase tracking-wider text-stone-400">or</span>
            <div className="h-px flex-1 bg-stone-200" />
          </div>
          <GoogleButton label="Sign in with Google" testId="google-signin-btn" />

          <div className="my-6 flex items-center gap-3">
            <div className="h-px flex-1 bg-stone-200" />
            <span className="font-mono text-[10px] uppercase tracking-wider text-stone-400">or redeem</span>
            <div className="h-px flex-1 bg-stone-200" />
          </div>
          <AccessCodeChip variant="dark" testId="login-access-code-chip" />
          <div className="mt-8 border-t border-stone-200 pt-6 text-sm text-stone-600">
            Don't have an account?{" "}
            <Link data-testid={AUTH.loginToSignup} to="/signup" className="font-medium text-slate-950 hover:underline">
              Create one
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

const inputCls = "w-full rounded-md border border-stone-300 bg-white px-3 py-2.5 text-sm text-slate-950 outline-none transition-all focus:border-slate-900 focus:ring-2 focus:ring-slate-900/10";
